import { useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import {
  resolveShowcaseSubPageSlugs,
  type ShowcaseSubPage,
  type ShowcaseSubPageWithSlug,
} from '~/components/v2/components/showcase/showcaseSubPageRoutes';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

// Module scope so an un-authored link doesn't hand a fresh array to the resolver on every render.
const EMPTY_SUB_PAGES: ShowcaseSubPage[] = [];

/**
 * The sub-page the viewer is currently INSIDE, with its canonical slug — including on a board they
 * drilled into from one (the URL surfaces carry that as the board route's `?page={slug}` trail,
 * the editor as its active page).
 *
 * Deliberately separate from `usePublicShowcaseActiveSubPage`, which decides what the collection
 * page RENDERS and therefore only ever trusts the path's `/page/{slug}` segment. This hook answers
 * "where in the site am I" for the navigation chrome — breadcrumbs and the nav bar's active state —
 * and reads the layout contract's `activePage`, which each surface resolves for itself: the
 * live page and the Preview iframe from their routes, the Edit tab from the editor's active page.
 */
export const usePublicShowcaseCurrentSubPage = (): ShowcaseSubPageWithSlug | null => {
  const { presentationPages } = useShortIdContext();
  const { activePage } = usePublicShowcaseContext();
  const subPages = presentationPages?.subPages ?? EMPTY_SUB_PAGES;
  const activePageSlug = activePage?.pageSlug ?? null;

  return useMemo(() => {
    if (!activePageSlug || !subPages.length) {
      return null;
    }

    // Resolve by SLUG, not collectionId: two sub-pages can share a collectionId, and the slug is
    // the identity the routes and nav entries use.
    return resolveShowcaseSubPageSlugs(subPages).find((entry) => entry.slug === activePageSlug) ?? null;
  }, [activePageSlug, subPages]);
};
